import { useCallback, useEffect, useMemo, useRef, useState, type UIEvent } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArchiveOutlined as ArchiveIcon,
  ArrowBack as ArrowBackIcon,
  CreateOutlined as ComposeIcon,
  Inbox as InboxIcon,
  MailOutline as MailOutlineIcon,
  Refresh as RefreshIcon,
  ReportGmailerrorredOutlined as SpamIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import SendSimpleOutboundDialog from '../grid/components/dialogs/SendSimpleOutboundDialog';
import {
  usePrepareSimpleOutboundReplyPreviewMutation,
  type ComposeRecipient,
  type SimpleOutboundPreview,
} from '../grid/api/simpleOutboundApi';
import { useConfirmDialog } from '../grid/hooks/useConfirmDialog';
import { isInboundFinanceEmailHistoryId } from '../grid/utils/financeEmailHistoryId';
import { extractErrorMessage } from '../../utils/errorUtils';
import { store } from '../../app/store';
import {
  financeEmailInboxApi,
  useGetFinanceEmailInboxThreadsInfiniteQuery,
  useMarkFinanceEmailInboxUnreadMutation,
} from './api/financeEmailInboxApi';
import { FinanceEmailThreadDetail } from './FinanceEmailThreadDetail';
import { SelectClientForComposeDialog } from './SelectClientForComposeDialog';
import { SendBulkSimpleOutboundDialog } from './SendBulkSimpleOutboundDialog';
import { composeRecipientToSimpleOutboundPreview } from './composeRecipientUtils';
import type { FinanceEmailInboxThreadListItem } from './types';
import { useFinanceEmailInboxMailboxActions } from './useFinanceEmailInboxMailboxActions';
import { avatarColor, formatListDate, initials } from './utils/threadDisplayUtils';
import { useDebouncedValue } from '../grid/hooks/useDebouncedValue';

type InboxSearch = {
  thread?: string;
  [key: string]: unknown;
};

const ThreadRow = ({
  thread,
  selected,
  checked,
  selectionMode,
  onSelect,
  onToggleChecked,
  onArchive,
  onSpam,
  actionsDisabled,
}: {
  thread: FinanceEmailInboxThreadListItem;
  selected: boolean;
  checked: boolean;
  selectionMode: boolean;
  onSelect: () => void;
  onToggleChecked: () => void;
  onArchive: (thread: FinanceEmailInboxThreadListItem) => void;
  onSpam: (thread: FinanceEmailInboxThreadListItem) => void;
  actionsDisabled?: boolean;
}) => {
  const unread = thread.unhandledCount > 0;
  const sender = thread.participants || thread.clientName || 'Unknown';
  const avatarSeed = thread.latestInboundFromAddress || sender;

  return (
    <ListItemButton
      selected={selected}
      onClick={onSelect}
      dense
      sx={{
        py: 1,
        px: 1.5,
        gap: 1,
        alignItems: 'flex-start',
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: unread ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
        transition: 'background-color 120ms ease',
        '&:hover, &:focus-within': {
          bgcolor: selected ? 'action.selected' : 'action.hover',
          '& .thread-row-date': { opacity: 0 },
          '& .thread-row-actions': { opacity: 1, pointerEvents: 'auto' },
          '& .thread-row-check': { opacity: 1 },
        },
        '&.Mui-selected': {
          bgcolor: 'rgba(25, 118, 210, 0.12)',
          borderLeft: '3px solid',
          borderLeftColor: 'primary.main',
          pl: '9px',
          '&:hover': {
            bgcolor: 'rgba(25, 118, 210, 0.16)',
          },
        },
      }}
    >
      <Checkbox
        className="thread-row-check"
        size="small"
        checked={checked}
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => {
          event.stopPropagation();
          onToggleChecked();
        }}
        sx={{
          p: 0.5,
          mt: 0.5,
          opacity: selectionMode || checked ? 1 : 0,
          transition: 'opacity 120ms ease',
        }}
        inputProps={{ 'aria-label': `Select ${thread.subject || 'conversation'}` }}
      />

      <Box
        sx={{
          width: 8,
          pt: 1.5,
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        {unread && (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'primary.main',
            }}
          />
        )}
      </Box>

      <Avatar
        sx={{
          width: 36,
          height: 36,
          fontSize: 13,
          fontWeight: 600,
          bgcolor: avatarColor(avatarSeed),
          flexShrink: 0,
          mt: 0.25,
        }}
      >
        {initials(avatarSeed)}
      </Avatar>

      <Box sx={{ width: '100%', minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography
            variant="body2"
            noWrap
            fontWeight={unread ? 700 : 500}
            sx={{ flex: 1, minWidth: 0 }}
          >
            {sender}
          </Typography>
          <Box sx={{ position: 'relative', flexShrink: 0, minWidth: 72, height: 28 }}>
            <Typography
              className="thread-row-date"
              variant="caption"
              sx={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                fontWeight: unread ? 700 : 400,
                color: unread ? 'primary.main' : 'text.secondary',
                transition: 'opacity 120ms ease',
                whiteSpace: 'nowrap',
              }}
            >
              {formatListDate(thread.lastOccurredAt)}
            </Typography>
            <Stack
              className="thread-row-actions"
              direction="row"
              spacing={0}
              sx={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                opacity: 0,
                pointerEvents: 'none',
                transition: 'opacity 120ms ease',
                bgcolor: 'background.paper',
                borderRadius: 1,
              }}
            >
              <Tooltip title="Archive">
                <span>
                  <IconButton
                    size="small"
                    disabled={actionsDisabled}
                    onClick={(event) => {
                      event.stopPropagation();
                      onArchive(thread);
                    }}
                    aria-label="Archive"
                  >
                    <ArchiveIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Report spam">
                <span>
                  <IconButton
                    size="small"
                    color="warning"
                    disabled={actionsDisabled || !thread.hasInbound}
                    onClick={(event) => {
                      event.stopPropagation();
                      onSpam(thread);
                    }}
                    aria-label="Report spam"
                  >
                    <SpamIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Box>
        </Stack>

        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.15 }}>
          <Typography
            variant="body2"
            noWrap
            fontWeight={unread ? 700 : 400}
            sx={{
              flex: '0 1 auto',
              maxWidth: '55%',
              minWidth: 0,
              color: 'text.primary',
            }}
          >
            {thread.subject || '(no subject)'}
          </Typography>
          <Typography variant="body2" noWrap color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
            {thread.preview ? `— ${thread.preview}` : ''}
          </Typography>
          {thread.messageCount > 1 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ flexShrink: 0, fontWeight: 600 }}
            >
              {thread.messageCount}
            </Typography>
          )}
        </Stack>

        {thread.clientName && (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            display="block"
            sx={{ mt: 0.15 }}
          >
            {thread.clientName}
            {unread && thread.unhandledCount > 1 ? ` · ${thread.unhandledCount} unread` : ''}
          </Typography>
        )}
      </Box>
    </ListItemButton>
  );
};

export const FinanceEmailInboxPage = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const searchParams = useSearch({ strict: false }) as InboxSearch;
  const threadFromUrl = typeof searchParams.thread === 'string' ? searchParams.thread.trim() : '';

  const [markUnread] = useMarkFinanceEmailInboxUnreadMutation();
  const [prepareReplyPreview] = usePrepareSimpleOutboundReplyPreviewMutation();
  const confirmDialog = useConfirmDialog();

  // Optimistic selection for instant UI; URL stays in sync for deep links / back-forward.
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(threadFromUrl || null);
  const selectedThreadIdRef = useRef(selectedThreadId);
  selectedThreadIdRef.current = selectedThreadId;

  const [checkedIds, setCheckedIds] = useState<Set<string>>(() => new Set());
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [isComposeClientDialogOpen, setIsComposeClientDialogOpen] = useState(false);
  const [composeRecipients, setComposeRecipients] = useState<ComposeRecipient[] | null>(null);
  const [outboundPreview, setOutboundPreview] = useState<SimpleOutboundPreview | null>(null);
  const [replyError, setReplyError] = useState<string | null>(null);
  const [isReplying, setIsReplying] = useState(false);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(
    null
  );

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useGetFinanceEmailInboxThreadsInfiniteQuery({
    search: debouncedSearch.trim() || undefined,
    unreadOnly,
  });

  const threads = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);
  const unreadCount = data?.pages[0]?.unreadCount ?? 0;
  const totalCount = data?.pages[0]?.totalCount ?? threads.length;

  const mailbox = useFinanceEmailInboxMailboxActions({
    requestConfirmation: confirmDialog.request,
    onNotify: (message, severity) => setToast({ message, severity }),
  });

  // Always refresh inbox data when entering this tab
  useEffect(() => {
    store.dispatch(financeEmailInboxApi.util.invalidateTags(['FinanceEmailInbox']));
  }, []);

  useEffect(() => {
    setSelectedThreadId(threadFromUrl || null);
  }, [threadFromUrl]);

  const selectThread = useCallback(
    (threadId: string | null) => {
      const nextId = threadId?.trim() || '';
      setSelectedThreadId(nextId || null);
      if (nextId === threadFromUrl) return;
      void navigate({
        // Keep current path (inbox is a menu splat page).
        to: '.',
        search: ((prev: InboxSearch) => {
          const next: InboxSearch = { ...prev };
          if (nextId) {
            next.thread = nextId;
          } else {
            delete next.thread;
          }
          return next;
        }) as never,
        replace: true,
      });
    },
    [navigate, threadFromUrl]
  );

  const handleThreadResolved = useCallback(
    (canonicalId: string, requestedId: string) => {
      // Only rewrite while the user is still on the deep-linked request that needed resolving.
      if (!canonicalId || selectedThreadIdRef.current !== requestedId) return;
      if (canonicalId === requestedId) return;
      selectThread(canonicalId);
    },
    [selectThread]
  );

  const resolveThreadHistoryIds = useCallback((thread: FinanceEmailInboxThreadListItem) => {
    const allIds =
      thread.historyIds?.filter(Boolean) ??
      (thread.latestHistoryId ? [thread.latestHistoryId] : []);
    const inboundIds =
      thread.inboundHistoryIds?.filter(Boolean) ??
      allIds.filter((id) => isInboundFinanceEmailHistoryId(id));
    return { allIds, inboundIds };
  }, []);

  const handleListArchive = useCallback(
    async (thread: FinanceEmailInboxThreadListItem) => {
      const { allIds } = resolveThreadHistoryIds(thread);
      const ok = await mailbox.archiveMessages(allIds);
      if (ok) {
        setCheckedIds((current) => {
          const next = new Set(current);
          next.delete(thread.threadId);
          return next;
        });
        if (selectedThreadId === thread.threadId) {
          selectThread(null);
        }
      }
    },
    [mailbox, resolveThreadHistoryIds, selectThread, selectedThreadId]
  );

  const handleListSpam = useCallback(
    async (thread: FinanceEmailInboxThreadListItem) => {
      const { inboundIds } = resolveThreadHistoryIds(thread);
      const ok = await mailbox.markSpamMessages(inboundIds);
      if (ok) {
        setCheckedIds((current) => {
          const next = new Set(current);
          next.delete(thread.threadId);
          return next;
        });
        if (selectedThreadId === thread.threadId) {
          selectThread(null);
        }
      }
    },
    [mailbox, resolveThreadHistoryIds, selectThread, selectedThreadId]
  );

  const handleMarkUnread = useCallback(
    async (historyIds: string[]) => {
      if (historyIds.length === 0) return;
      try {
        const message = await markUnread({
          historyIds,
          threadId: selectedThreadId ?? undefined,
        }).unwrap();
        setToast({ message: message || 'Marked as unread.', severity: 'success' });
      } catch (err) {
        setToast({
          message: extractErrorMessage(err, 'Failed to mark as unread.'),
          severity: 'error',
        });
      }
    },
    [markUnread, selectedThreadId]
  );

  const checkedThreads = useMemo(
    () => threads.filter((thread) => checkedIds.has(thread.threadId)),
    [checkedIds, threads]
  );

  const allVisibleChecked =
    threads.length > 0 && threads.every((thread) => checkedIds.has(thread.threadId));
  const someVisibleChecked = threads.some((thread) => checkedIds.has(thread.threadId));

  useEffect(() => {
    setCheckedIds((current) => {
      if (current.size === 0) return current;
      const valid = new Set(threads.map((thread) => thread.threadId));
      const next = new Set([...current].filter((id) => valid.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [threads]);

  const handleListScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const el = event.currentTarget;
      if (el.scrollTop + el.clientHeight < el.scrollHeight - 96) return;
      if (!hasNextPage || isFetchingNextPage || isFetching) return;
      void fetchNextPage();
    },
    [fetchNextPage, hasNextPage, isFetching, isFetchingNextPage]
  );

  const toggleChecked = useCallback((threadId: string) => {
    setCheckedIds((current) => {
      const next = new Set(current);
      if (next.has(threadId)) next.delete(threadId);
      else next.add(threadId);
      return next;
    });
  }, []);

  const toggleSelectAllVisible = useCallback(() => {
    setCheckedIds((current) => {
      if (threads.length === 0) return current;
      const allChecked = threads.every((thread) => current.has(thread.threadId));
      if (allChecked) {
        const next = new Set(current);
        for (const thread of threads) next.delete(thread.threadId);
        return next;
      }
      const next = new Set(current);
      for (const thread of threads) next.add(thread.threadId);
      return next;
    });
  }, [threads]);

  const handleBulkArchive = useCallback(async () => {
    if (checkedThreads.length === 0) return;
    const allIds = [
      ...new Set(checkedThreads.flatMap((thread) => resolveThreadHistoryIds(thread).allIds)),
    ];
    const ok = await mailbox.archiveMessages(allIds);
    if (ok) {
      const removed = new Set(checkedThreads.map((thread) => thread.threadId));
      setCheckedIds(new Set());
      if (selectedThreadId && removed.has(selectedThreadId)) {
        selectThread(null);
      }
    }
  }, [checkedThreads, mailbox, resolveThreadHistoryIds, selectThread, selectedThreadId]);

  const handleBulkSpam = useCallback(async () => {
    if (checkedThreads.length === 0) return;
    const inboundIds = [
      ...new Set(checkedThreads.flatMap((thread) => resolveThreadHistoryIds(thread).inboundIds)),
    ];
    const ok = await mailbox.markSpamMessages(inboundIds);
    if (ok) {
      const removed = new Set(checkedThreads.map((thread) => thread.threadId));
      setCheckedIds(new Set());
      if (selectedThreadId && removed.has(selectedThreadId)) {
        selectThread(null);
      }
    }
  }, [checkedThreads, mailbox, resolveThreadHistoryIds, selectThread, selectedThreadId]);

  const handleReply = async (historyId: string, replyAll = false) => {
    setReplyError(null);
    setIsReplying(true);
    try {
      const preview = await prepareReplyPreview({ historyId, replyAll }).unwrap();
      setOutboundPreview(preview);
    } catch (err) {
      setReplyError(extractErrorMessage(err, 'Failed to prepare reply.'));
    } finally {
      setIsReplying(false);
    }
  };

  const handleComposeRecipientsSelected = (recipients: ComposeRecipient[]) => {
    setIsComposeClientDialogOpen(false);
    if (recipients.length === 1) {
      setComposeRecipients(null);
      setOutboundPreview(composeRecipientToSimpleOutboundPreview(recipients[0]));
      return;
    }
    setOutboundPreview(null);
    setComposeRecipients(recipients);
  };

  const handleOutboundSuccess = (message: string) => {
    setOutboundPreview(null);
    setComposeRecipients(null);
    setToast({ message, severity: 'success' });
    store.dispatch(financeEmailInboxApi.util.invalidateTags(['FinanceEmailInbox']));
    void refetch();
  };

  const showList = isDesktop || !selectedThreadId;
  const showDetail = isDesktop || Boolean(selectedThreadId);
  const selectionMode = checkedIds.size > 0;

  const listPane = (
    <Box
      sx={{
        width: isDesktop ? 400 : '100%',
        flexShrink: 0,
        display: showList ? 'flex' : 'none',
        flexDirection: 'column',
        minHeight: 0,
        borderRight: isDesktop ? '1px solid' : 'none',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Stack
        spacing={1}
        sx={{
          px: 1.5,
          pt: 1.25,
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            fullWidth
            placeholder="Search mail"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: (t) => (t.palette.mode === 'dark' ? 'action.hover' : 'grey.100'),
                '& fieldset': { borderColor: 'transparent' },
                '&:hover fieldset': { borderColor: 'divider' },
                '&.Mui-focused': {
                  bgcolor: 'background.paper',
                  '& fieldset': { borderColor: 'primary.main' },
                },
              },
            }}
          />
          <Tooltip title="Compose">
            <span>
              <IconButton
                size="small"
                color="primary"
                onClick={() => setIsComposeClientDialogOpen(true)}
                aria-label="Compose"
              >
                <ComposeIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Refresh">
            <span>
              <IconButton
                size="small"
                onClick={() => void refetch()}
                disabled={isFetching}
                aria-label="Refresh"
              >
                {isFetching ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        {selectionMode ? (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Checkbox
              size="small"
              checked={allVisibleChecked}
              indeterminate={someVisibleChecked && !allVisibleChecked}
              onChange={toggleSelectAllVisible}
              sx={{ p: 0.5 }}
              inputProps={{ 'aria-label': 'Select all conversations' }}
            />
            <Typography variant="body2" fontWeight={600}>
              {checkedIds.size} selected
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Archive selected">
              <span>
                <IconButton
                  size="small"
                  disabled={mailbox.isActing}
                  onClick={() => void handleBulkArchive()}
                  aria-label="Archive selected"
                >
                  <ArchiveIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Report selected as spam">
              <span>
                <IconButton
                  size="small"
                  color="warning"
                  disabled={mailbox.isActing}
                  onClick={() => void handleBulkSpam()}
                  aria-label="Report selected as spam"
                >
                  <SpamIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              size="small"
              onClick={() => setCheckedIds(new Set())}
              sx={{ textTransform: 'none', minWidth: 0 }}
            >
              Clear
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Chip
              size="small"
              label="All"
              clickable
              color={!unreadOnly ? 'primary' : 'default'}
              variant={!unreadOnly ? 'filled' : 'outlined'}
              onClick={() => setUnreadOnly(false)}
              sx={{ height: 24 }}
            />
            <Chip
              size="small"
              label={unreadCount > 0 ? `Unread (${unreadCount})` : 'Unread'}
              clickable
              color={unreadOnly ? 'primary' : 'default'}
              variant={unreadOnly ? 'filled' : 'outlined'}
              onClick={() => setUnreadOnly(true)}
              sx={{ height: 24 }}
            />
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.secondary">
              {threads.length}
              {totalCount > threads.length ? ` of ${totalCount}` : ''}{' '}
              {totalCount === 1 ? 'conversation' : 'conversations'}
            </Typography>
          </Stack>
        )}
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }} onScroll={handleListScroll}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {isError && (
          <Alert
            severity="error"
            sx={{ m: 1.5 }}
            action={
              <Button color="inherit" size="small" onClick={() => void refetch()}>
                Retry
              </Button>
            }
          >
            {error instanceof Error ? error.message : 'Failed to load inbox threads.'}
          </Alert>
        )}

        {!isLoading && !isError && threads.length === 0 && (
          <Stack alignItems="center" spacing={1} sx={{ py: 8, px: 3, color: 'text.secondary' }}>
            <MailOutlineIcon sx={{ fontSize: 48, opacity: 0.45 }} />
            <Typography variant="subtitle1" fontWeight={600} color="text.primary">
              {search || unreadOnly ? 'No matching conversations' : 'Your inbox is empty'}
            </Typography>
            <Typography variant="body2" textAlign="center">
              {search || unreadOnly
                ? 'Try a different search or clear the Unread filter.'
                : 'New finance email conversations will appear here.'}
            </Typography>
          </Stack>
        )}

        {!isLoading && !isError && threads.length > 0 && (
          <List disablePadding>
            {threads.map((thread) => (
              <ThreadRow
                key={thread.threadId}
                thread={thread}
                selected={
                  thread.threadId === selectedThreadId ||
                  thread.latestHistoryId === selectedThreadId
                }
                checked={checkedIds.has(thread.threadId)}
                selectionMode={selectionMode}
                onSelect={() => selectThread(thread.threadId)}
                onToggleChecked={() => toggleChecked(thread.threadId)}
                onArchive={(item) => void handleListArchive(item)}
                onSpam={(item) => void handleListSpam(item)}
                actionsDisabled={mailbox.isActing}
              />
            ))}
          </List>
        )}

        {(isFetchingNextPage || (isFetching && threads.length > 0 && hasNextPage)) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        )}
      </Box>
    </Box>
  );

  const detailPane = (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        display: showDetail ? 'flex' : isDesktop ? 'flex' : 'none',
        flexDirection: 'column',
        bgcolor: (t) => (t.palette.mode === 'dark' ? 'background.default' : 'grey.50'),
      }}
    >
      {!selectedThreadId && isDesktop && (
        <Stack
          spacing={1.5}
          alignItems="center"
          justifyContent="center"
          sx={{ flex: 1, color: 'text.secondary', p: 4 }}
        >
          <InboxIcon sx={{ fontSize: 56, opacity: 0.35 }} />
          <Typography variant="h6" color="text.primary" fontWeight={500}>
            Select a conversation
          </Typography>
          <Typography variant="body2" textAlign="center" sx={{ maxWidth: 320 }}>
            Choose a thread from the list to read messages and reply.
          </Typography>
        </Stack>
      )}

      {selectedThreadId && (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {!isDesktop && (
            <Box
              sx={{
                px: 0.5,
                py: 0.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => selectThread(null)}
                sx={{ textTransform: 'none' }}
              >
                Inbox
              </Button>
            </Box>
          )}
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <FinanceEmailThreadDetail
              key={selectedThreadId}
              threadId={selectedThreadId}
              onBack={() => selectThread(null)}
              onReply={(historyId, replyAll) => void handleReply(historyId, replyAll)}
              replyError={replyError}
              isReplying={isReplying}
              showBackButton={false}
              onThreadResolved={handleThreadResolved}
              mailbox={{
                onArchive: (historyIds) => {
                  void mailbox.archiveMessages(historyIds).then((ok) => {
                    if (ok) selectThread(null);
                  });
                },
                onSpam: (historyIds) => {
                  void mailbox.markSpamMessages(historyIds).then((ok) => {
                    if (ok) selectThread(null);
                  });
                },
                onBlockSender: (historyId, fromAddress) =>
                  void mailbox.addSenderToSpamList(historyId, fromAddress).then((ok) => {
                    if (ok) selectThread(null);
                  }),
                onMarkUnread: (historyIds) => void handleMarkUnread(historyIds),
                isActing: mailbox.isActing,
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
        {listPane}
        {detailPane}
      </Box>

      {isComposeClientDialogOpen && (
        <SelectClientForComposeDialog
          open
          onClose={() => setIsComposeClientDialogOpen(false)}
          onContinue={handleComposeRecipientsSelected}
        />
      )}

      {composeRecipients && composeRecipients.length > 1 && (
        <SendBulkSimpleOutboundDialog
          open
          recipients={composeRecipients}
          onClose={() => setComposeRecipients(null)}
          onSuccess={handleOutboundSuccess}
        />
      )}

      {outboundPreview && (
        <SendSimpleOutboundDialog
          open
          clientId={outboundPreview.clientId}
          preview={outboundPreview}
          onClose={() => setOutboundPreview(null)}
          onSuccess={handleOutboundSuccess}
          showEmailSourceToggle={false}
        />
      )}

      <ConfirmDialog
        open={confirmDialog.state.open}
        message={confirmDialog.state.message}
        onConfirm={confirmDialog.confirm}
        onClose={confirmDialog.cancel}
        loading={mailbox.isActing}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast?.severity ?? 'success'}
          onClose={() => setToast(null)}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FinanceEmailInboxPage;
